import {formatToolResultPlainText} from './formatToolInputForDisplay'

type CwlListWorkflow = {display_name: string; tool_name?: string}
type CwlListPayload = {workflows?: CwlListWorkflow[]}

type CwlParamsPayload = {
  display_name?: string
  tool_name?: string
  parameters?: Record<string, unknown>
  input_spec?: Record<string, unknown>
  validation_hint?: string
  note?: string
}

function parseJsonPayload<T>(raw: unknown): T | null {
  if (raw == null || raw === '') return null
  const text = typeof raw === 'string' ? raw.trim() : ''
  if (!text) return null
  try {
    return JSON.parse(text) as T
  } catch {
    return null
  }
}

/** User-facing result text for list_cwl_workflows (display names only). */
export function formatListCwlWorkflowsResult(result: unknown): string {
  const data = parseJsonPayload<CwlListPayload>(result)
  if (!data?.workflows?.length) {
    return formatToolResultPlainText(result)
  }
  return data.workflows.map((w) => `- ${w.display_name}`).join('\n')
}

/** User-facing result text for get_cwl_workflow_params (no internal ids). */
export function formatGetCwlWorkflowParamsResult(result: unknown): string {
  const data = parseJsonPayload<CwlParamsPayload>(result)
  if (!data) {
    return formatToolResultPlainText(result)
  }
  const lines: string[] = []
  if (data.display_name) {
    lines.push(`Workflow: ${data.display_name}`)
  }
  if (data.input_spec && Object.keys(data.input_spec).length > 0) {
    lines.push('')
    lines.push('Input specification:')
    lines.push(JSON.stringify(data.input_spec, null, 2))
  }
  if (data.parameters && Object.keys(data.parameters).length > 0) {
    lines.push('')
    lines.push('Example parameters:')
    lines.push(JSON.stringify(data.parameters, null, 2))
  }
  if (data.validation_hint) {
    lines.push('')
    lines.push(data.validation_hint)
  }
  return lines.join('\n').trim() || formatToolResultPlainText(result)
}

/** Summary line for get_cwl_workflow_params / run_cwl_workflow from args or result. */
export function cwlWorkflowSummaryFromTool(
  toolName: string,
  args: Record<string, unknown> | undefined,
  result: unknown,
): string | undefined {
  const key = toolName.trim().toLowerCase()
  if (key === 'get_cwl_workflow_params') {
    const data = parseJsonPayload<CwlParamsPayload>(result)
    if (data?.display_name) return data.display_name
    const tn = args?.tool_name
    return typeof tn === 'string' ? formatInternalRunToolSlug(tn) : undefined
  }
  if (key === 'run_cwl_workflow') {
    const data = parseJsonPayload<CwlParamsPayload>(result)
    if (data?.display_name) return data.display_name
    const tn = args?.tool_name
    return typeof tn === 'string' ? formatInternalRunToolSlug(tn) : undefined
  }
  return undefined
}

/** Best-effort label from run_* tool_name when display_name is not yet available. */
function formatInternalRunToolSlug(toolName: string): string {
  const m = /^run_(.+)$/.exec(toolName.trim())
  if (!m) return toolName
  return m[1].replace(/_/g, ' ')
}
